/**
 *  @swagger
 *  definitions:
 *    RegattaCourseParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Name:
 *          description: Name of the RegattaCourse
 *          type: string
 *        ShowMap:
 *          description: Show Map of the RegattaCourse
 *          type: boolean
 *
 *  @swagger
 *  definitions:
 *    RegattaCourseCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaCourseParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Name
 *            - ShowMap
 *
 *  @swagger
 *  definitions:
 *    RegattaCourseQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaCourse
 *              type: string
 *        - $ref: '#/definitions/RegattaCourseParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaCourseUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaCourseQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaCourse:
 *      allOf:
 *        - $ref: '#/definitions/RegattaCourseCreateParams'
 *        - $ref: '#/definitions/RegattaCourseUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaCourse:
 *        allOf:
 *          - $ref: '#/definitions/RegattaCourse'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaCourse
 *                $ref: '#/components/schemas/Regatta'
 *              RegattaCoursePoints:
 *                description: RegattaCoursePoints of the RegattaCourse
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaCoursePoint'
 *                $ref: '#/components/schemas/Regatta'
 *              RegattaCourseRaces:
 *                description: RegattaCourseRaces of the RegattaCourse
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaCourseRace'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Name: string;
  ShowMap: boolean;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
  RegattaCoursePoints?: Array<$Model<'RegattaCoursePoint'>>;
  RegattaCourseRaces?: Array<$Model<'RegattaCourseRace'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ShowMap: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaCourse'> => {
  const RegattaCourse = stack.define(
    'RegattaCourse',
    fields(Sequelize),
    {
      tableName: 'RegattaCourses',
    },
  );

  RegattaCourse.associate = (models: $ModelClasses) => {
    models.RegattaCourse.hasMany(
      models.RegattaCoursePoint,
      {
        as: 'RegattaCoursePoints',
        foreignKey: 'RegattaCourseId',
      },
    );
    models.RegattaCourse.hasMany(
      models.RegattaCourseRace,
      {
        as: 'RegattaCourseRaces',
        foreignKey: 'RegattaCourseId',
      },
    );
    models.RegattaCourse.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaCourse.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaCourse;
});
